import React, { useState } from 'react'
import { AlertTriangle, CheckCircle2, HelpCircle, DollarSign, Eye, User, Clock, BarChart2, ChevronDown, ChevronRight } from 'lucide-react'
import { BANK_FOR_TYPOLOGY } from '../typologyData/scoredBanks'
import { useTypology } from '../../../hooks/useTypology'

const C = {
  pri: '#0a5169', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc',
  ink: '#0f172a',
}

export default function InvestigationScoredStrategy({ caseData, patron, transactions = [], overview, fmt$, typology = 'structuring' }) {
  // DB-first: a tenant admin can tune scored_bank in the typologies table.
  // While loading, or if the DB row has no scored_bank, fall back to the
  // bundled JS so the strategy renders instantly with sensible defaults.
  const dbTypology = useTypology(typology)
  const BANK = (dbTypology && dbTypology.scored_bank)
    || BANK_FOR_TYPOLOGY[typology]
    || BANK_FOR_TYPOLOGY.structuring
  const allQuestions = BANK.sections.flatMap(s => s.questions)
  const fmt = fmt$ || ((n) => '$' + (n ?? 0).toLocaleString())
  const txTotal = transactions.reduce((a, t) => a + (t.amount || 0), 0)
  const profile = overview?.profile || {}
  const [answers, setAnswers] = useState({})
  const [expandedSections, setExpandedSections] = useState({ 0: true, 1: false, 2: false, 3: false })

  const answered = Object.keys(answers).length
  const total = allQuestions.length
  const score = allQuestions.reduce((sum, q) => sum + (answers[q.id] !== undefined ? (q.options[answers[q.id]]?.score || 0) : 0), 0)
  const minPossible = allQuestions.reduce((s, q) => s + Math.min(...q.options.map(o => o.score)), 0)
  const pct = Math.max(0, Math.min(100, ((score - minPossible) / (BANK.maxScore - minPossible)) * 100))

  const lean = score >= BANK.thresholds.suspicious
    ? { label: 'Leans Suspicious', color: C.red, bg: C.redBg, bd: C.redBd, icon: AlertTriangle }
    : score >= BANK.thresholds.inconclusive
    ? { label: 'Inconclusive', color: C.amber, bg: C.amberBg, bd: C.amberBd, icon: HelpCircle }
    : { label: 'Leans Innocent', color: C.green, bg: C.greenBg, bd: C.greenBd, icon: CheckCircle2 }

  const sectionScore = (section) => section.questions.reduce((sum, q) => sum + (answers[q.id] !== undefined ? (q.options[answers[q.id]]?.score || 0) : 0), 0)
  const sectionComplete = (section) => section.questions.every(q => answers[q.id] !== undefined)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
      <div>
        <div style={{ background: BANK.colour + '0d', borderLeft: `4px solid ${BANK.colour}`, borderRadius: 8, padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <BarChart2 size={15} color={BANK.colour} style={{ marginTop: 1, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: BANK.colour, marginBottom: 4 }}>Assessment: {BANK.label}</div>
              <div style={{ fontSize: 12, color: C.slate, lineHeight: 1.6 }}>{BANK.intro}</div>
              {(patron?.name || patron?.occupation || transactions?.length > 0) && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.08)', fontSize: 11, color: C.slate, lineHeight: 1.5 }}>
                  <strong style={{ color: C.ink }}>Case context:</strong> {patron?.name || 'Patron'} — {profile?.occupation || patron?.occupation || '—'} | Risk: {patron?.risk ?? 0}/100 | {transactions?.length || 0} linked transactions, total {fmt(txTotal)}
                </div>
              )}
            </div>
          </div>
        </div>

        {BANK.sections.map((section, si) => {
          const SIcon = section.icon
          const isOpen = expandedSections[si]
          const complete = sectionComplete(section)
          return (
            <div key={si} style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
              <div onClick={() => setExpandedSections(prev => ({ ...prev, [si]: !prev[si] }))} style={{ padding: '13px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, background: isOpen ? '#fafbfc' : '#fff', borderBottom: isOpen ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: complete ? C.greenBg : C.priM, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {complete ? <CheckCircle2 size={15} color={C.green} /> : <SIcon size={15} color={C.pri} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{section.title}</div>
                  <div style={{ fontSize: 11, color: C.slateL }}>{section.questions.filter(q => answers[q.id] !== undefined).length} of {section.questions.length} answered</div>
                </div>
                {isOpen ? <ChevronDown size={15} color={C.slateL} /> : <ChevronRight size={15} color={C.slateL} />}
              </div>
              {isOpen && (
                <div style={{ padding: '16px 18px' }}>
                  {section.questions.map((q, qi) => (
                    <div key={q.id} style={{ marginBottom: qi < section.questions.length - 1 ? 22 : 0, paddingBottom: qi < section.questions.length - 1 ? 22 : 0, borderBottom: qi < section.questions.length - 1 ? '1px solid #f8f8f8' : 'none' }}>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, lineHeight: 1.4, marginBottom: 4 }}><span style={{ color: C.slateL, marginRight: 6 }}>Q{allQuestions.indexOf(q) + 1}.</span>{q.text}</div>
                        {q.hint && <div style={{ fontSize: 11, color: C.slate, background: C.slateBg, border: '1px solid #e8eef2', borderRadius: 5, padding: '6px 10px', lineHeight: 1.5 }}><span style={{ fontWeight: 700, color: C.pri }}>Guide: </span>{q.hint}</div>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {q.options.map((opt, oi) => {
                          const isSelected = answers[q.id] === oi
                          const isPositive = opt.score > 0
                          const isNeutral = opt.score === 0
                          return (
                            <div key={oi} onClick={() => setAnswers(prev => ({ ...prev, [q.id]: oi }))} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${isSelected ? (isPositive ? C.redBd : isNeutral ? C.amberBd : C.greenBd) : '#e8eef2'}`, background: isSelected ? (isPositive ? C.redBg : isNeutral ? C.amberBg : C.greenBg) : '#fff' }}>
                              <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${isSelected ? (isPositive ? C.red : isNeutral ? C.amber : C.green) : '#d1d5db'}`, background: isSelected ? (isPositive ? C.red : isNeutral ? C.amber : C.green) : '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{isSelected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}</div>
                              <span style={{ flex: 1, fontSize: 12, color: isSelected ? C.ink : '#475569', fontWeight: isSelected ? 600 : 400 }}>{opt.label}</span>
                              <span style={{ fontSize: 11, fontWeight: 700, color: isSelected ? (isPositive ? C.red : isNeutral ? C.amber : C.green) : C.slateL }}>{opt.score > 0 ? '+' : ''}{opt.score}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div style={{ position: 'sticky', top: 20, alignSelf: 'flex-start' }}>
        <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart2 size={14} color={C.pri} />
            <div><div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>Assessment Score</div><div style={{ fontSize: 11, color: C.slateL }}>{answered} of {total} questions answered</div></div>
          </div>
          <div style={{ padding: '16px 18px' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 52, fontWeight: 900, color: score >= BANK.thresholds.suspicious ? C.red : score >= BANK.thresholds.inconclusive ? C.amber : C.green, lineHeight: 1 }}>{score > 0 ? '+' : ''}{score}</div>
              <div style={{ fontSize: 11, color: C.slateL, marginTop: 4 }}>Threshold: {BANK.thresholds.inconclusive} inconclusive · {BANK.thresholds.suspicious} suspicious</div>
            </div>
            <div style={{ height: 12, background: 'linear-gradient(to right, #dcfce7, #fef9c3, #fee2e2)', borderRadius: 6, position: 'relative', border: '1px solid #e8eef2', marginBottom: 14 }}>
              <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: `${pct}%`, width: 18, height: 18, borderRadius: '50%', background: score >= BANK.thresholds.suspicious ? C.red : score >= BANK.thresholds.inconclusive ? C.amber : C.green, border: '3px solid #fff', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', marginLeft: -9 }} />
            </div>
            {answered >= 4 && (
              <div style={{ background: lean.bg, border: `1px solid ${lean.bd}`, borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <lean.icon size={15} color={lean.color} />
                  <span style={{ fontSize: 13, fontWeight: 800, color: lean.color }}>{lean.label}</span>
                </div>
                <div style={{ fontSize: 10, color: C.slateL, marginTop: 8 }}>{total - answered > 0 ? `${total - answered} questions remaining` : 'All questions answered'}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
